/**
 * Cart Promo Cleanup — 공통 SSOT 헬퍼
 *
 * 카트의 promo properties / catalog-added GY 라인 / 활성 discount_codes 를 한 번에 정리.
 * 호출처:
 *   - guest-cart-promo-guard.js : 비로그인 진입 시 자동 cleanup
 *   - employee-payment-popup.js : 사원판매 모드 ON 전환 시 cleanup
 *
 * 정리 범위 (순서 중요):
 *   1) catalog-added GY 라인 (_promotion_added_by_bxgy='true') — cart 라인 자체 제거
 *   2) 나머지 promo 적용 라인 (_promotion_*) — properties wipe (라인 보존, 정가 회복)
 *   3) 활성 discount_codes — applyDiscountCode('')
 *
 * 회귀 정책 SSOT: wiki/docs/cart-promo-cleanup-policy.md (TBD)
 */

(function () {
  'use strict';

  async function fetchCart() {
    try {
      const res = await fetch(MISTO.localeUrl('/cart.js'), { credentials: 'same-origin' });
      if (!res.ok) return null;
      return await res.json();
    } catch (err) {
      console.warn('[cart-promo-cleanup] fetchCart', err);
      return null;
    }
  }

  function isPromoKey(key) {
    return key.indexOf('_promotion_') === 0 || key.indexOf('promotion_') === 0;
  }

  // 🔴 (2026-05-28 #25 fix) Promo clear = cart-promo-effects.js 의 clearLineByKey SSOT 재사용.
  //   Shopify cart change.js 가 hidden `_` prefix properties 빈 객체 변경 불가 → __none__ sentinel 패턴.
  //   cart-promo-effects.js 가 SSOT (promoClearTransformer + clearLineByKey). 여기선 그 헬퍼 호출만.
  //   의존성: cart-promo-effects.js 가 cart-promo-cleanup.js 보다 먼저 로드 (layout/theme.liquid 순서).
  async function wipeLineProperties(line) {
    try {
      if (!window.CartPromoEffects || typeof window.CartPromoEffects.clearLineByKey !== 'function') {
        console.warn('[cart-promo-cleanup] CartPromoEffects.clearLineByKey unavailable — load order 점검');
        return;
      }
      await window.CartPromoEffects.clearLineByKey(line.key, line.variant_id);
    } catch (err) {
      console.warn('[cart-promo-cleanup] wipeLineProperties', err);
    }
  }

  async function run(opts) {
    opts = opts || {};
    const cart = await fetchCart();
    if (!cart) {
      console.warn('[cart-promo-cleanup] ❌ no cart');
      return { cleaned: false, reason: 'no-cart' };
    }

    const items = cart.items || [];
    const catalogAddedKeys = [];
    const promoLines = [];
    for (const item of items) {
      const props = item.properties || {};
      // 🔴 (2026-05-28) BXGY 자식 식별 강화 — added_by_bxgy 또는 role='get' 둘 다 catalog-added 로 처리.
      //    GY 신규행 only 정책상 role='get' 인 라인은 모두 catalog add. 단 한쪽 prop 누락된 회귀 case 대비.
      if (props._promotion_added_by_bxgy === 'true' || props._promotion_bxgy_role === 'get') {
        catalogAddedKeys.push(item.key);
      } else if (props._promotion_id || props._promotion_code || props._promotion_title) {
        promoLines.push({ key: item.key, variant_id: item.variant_id });
      }
    }
    const hasDiscount = (cart.discount_codes || []).length > 0;

    if (!catalogAddedKeys.length && !promoLines.length && !hasDiscount) {
      return { cleaned: false, reason: 'already-clean' };
    }

    // (1) + (3): catalog-added 라인 제거 + discount clear — 한 호출에 묶음
    const updatePayload = {};
    if (catalogAddedKeys.length) {
      updatePayload.updates = catalogAddedKeys.reduce((acc, key) => {
        acc[key] = 0;
        return acc;
      }, {});
    }
    if (hasDiscount) {
      updatePayload.discount = '';
    }
    if (Object.keys(updatePayload).length) {
      try {
        await fetch(MISTO.localeUrl('/cart/update.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify(updatePayload),
        });
      } catch (err) {
        console.warn('[cart-promo-cleanup] ❌ update.js failed', err);
      }
    }

    // (2): properties wipe — line key 가 change 마다 바뀌므로 sequential
    for (const line of promoLines) {
      await wipeLineProperties(line);
    }

    const result = {
      cleaned: true,
      removedGy: catalogAddedKeys.length,
      wipedLines: promoLines.length,
      discountCleared: hasDiscount,
    };
    return result;
  }

  function refreshDrawerSections() {
    try {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.getSectionsToRender === 'function' && typeof drawer.renderContents === 'function') {
        const sectionNames = drawer.getSectionsToRender().map((s) => s.section).join(',');
        fetch(MISTO.localeUrl('/cart?sections=') + encodeURIComponent(sectionNames) + '&sections_url=' + encodeURIComponent(window.location.pathname))
          .then((r) => r.json())
          .then((sectionsData) => drawer.renderContents({ sections: sectionsData }, false))
          .catch((err) => console.warn('[cart-promo-cleanup] drawer refresh', err));
      }
      if (typeof window.updateCartCounters === 'function') window.updateCartCounters();
    } catch (err) {
      console.warn('[cart-promo-cleanup] refreshDrawerSections', err);
    }
  }

  window.CartPromoCleanup = {
    run,
    refreshDrawerSections,
  };
})();
